/**
 * 1stbuddha — localization.js
 *
 * <localization-form> (snippets/localization-form.liquid)
 *   - dropdown variant: toggles the option list (aria-expanded), closes on
 *     Esc, outside click or focus leaving; ↑/↓ move between options.
 *     Options are submit buttons, so selecting one submits the form natively.
 *   - select variant: submits the form when a select changes; the language
 *     select ([data-navigate]) goes to the chosen language's URL instead.
 *   - language links: each points at the domain serving that language. When
 *     the page has an hreflang alternate for that language on that domain,
 *     its exact URL is used (translated handles); the query string and hash
 *     are kept.
 */
(() => {
  if (customElements.get('localization-form')) return;

  class LocalizationForm extends HTMLElement {
    constructor() {
      super();
      this.onDocumentClick = this.onDocumentClick.bind(this);
    }

    connectedCallback() {
      this.form = this.querySelector('form');
      this.resolveLanguageUrls();

      this.addEventListener('change', (event) => {
        const select = event.target;
        if (!select.matches('select')) return;
        if (select.hasAttribute('data-navigate')) {
          if (select.value) window.location.href = select.value;
          return;
        }
        this.form?.requestSubmit();
      });

      this.addEventListener('click', (event) => {
        const toggle = event.target.closest('.localization__toggle');
        if (toggle) this.toggle(toggle);
      });

      this.addEventListener('keydown', (event) => this.onKeydown(event));

      this.addEventListener('focusout', (event) => {
        const disclosure = event.target.closest('.localization__disclosure');
        if (disclosure && !disclosure.contains(event.relatedTarget)) {
          this.close(disclosure.querySelector('.localization__toggle'));
        }
      });
    }

    resolveLanguageUrls() {
      const alternates = [...document.querySelectorAll('link[rel="alternate"][hreflang]')];
      const suffix = window.location.search + window.location.hash;

      this.querySelectorAll('[data-lang]').forEach((el) => {
        const attr = el.tagName === 'OPTION' ? 'value' : 'href';
        const lang = el.dataset.lang.toLowerCase();
        let url;
        try {
          url = new URL(el.getAttribute(attr), window.location.href);
        } catch {
          return;
        }

        const match = alternates.find((link) => {
          const code = link.hreflang.toLowerCase();
          if (code !== lang && !code.startsWith(`${lang}-`)) return false;
          try {
            return new URL(link.href).origin === url.origin;
          } catch {
            return false;
          }
        });

        const base = match ? match.href : url.href;
        el.setAttribute(attr, base.split(/[?#]/)[0] + suffix);
      });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
    }

    toggle(toggle) {
      if (toggle.getAttribute('aria-expanded') === 'true') {
        this.close(toggle);
      } else {
        this.open(toggle);
      }
    }

    open(toggle) {
      this.querySelectorAll('.localization__toggle[aria-expanded="true"]').forEach((other) => this.close(other));
      const list = this.listFor(toggle);
      if (!list) return;

      toggle.setAttribute('aria-expanded', 'true');
      list.hidden = false;
      list.querySelector('[aria-current="true"]')?.scrollIntoView({ block: 'nearest' });
      document.addEventListener('click', this.onDocumentClick);
    }

    close(toggle, returnFocus = false) {
      if (!toggle || toggle.getAttribute('aria-expanded') !== 'true') return;
      toggle.setAttribute('aria-expanded', 'false');
      const list = this.listFor(toggle);
      if (list) list.hidden = true;
      if (returnFocus) toggle.focus();
      if (!this.querySelector('.localization__toggle[aria-expanded="true"]')) {
        document.removeEventListener('click', this.onDocumentClick);
      }
    }

    listFor(toggle) {
      return document.getElementById(toggle.getAttribute('aria-controls'));
    }

    onDocumentClick(event) {
      if (this.contains(event.target)) return;
      this.querySelectorAll('.localization__toggle[aria-expanded="true"]').forEach((toggle) => this.close(toggle));
    }

    onKeydown(event) {
      const disclosure = event.target.closest('.localization__disclosure');
      if (!disclosure) return;
      const toggle = disclosure.querySelector('.localization__toggle');

      if (event.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') {
        event.preventDefault();
        this.close(toggle, true);
        return;
      }

      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      event.preventDefault();

      if (toggle.getAttribute('aria-expanded') !== 'true') this.open(toggle);
      const options = [...disclosure.querySelectorAll('.localization__option')];
      const index = options.indexOf(document.activeElement);
      let next;
      if (index === -1) {
        next = disclosure.querySelector('.localization__option[aria-current="true"]') || options[0];
      } else {
        const step = event.key === 'ArrowDown' ? 1 : -1;
        next = options[Math.min(options.length - 1, Math.max(0, index + step))];
      }
      next?.focus();
    }
  }

  customElements.define('localization-form', LocalizationForm);
})();
